import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  Chip,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Stack,
  Container,
  useTheme,
  useMediaQuery,
  Fade,
  Slide
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Star as StarIcon,
  QrCode as QrCodeIcon,
  Language as LanguageIcon,
  Search as SearchIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Support as SupportIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import SubscriptionStatus from './SubscriptionStatus';
import { getFunctions, httpsCallable } from 'firebase/functions';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import { useTranslation } from '../../hooks/useTranslation';

const SubscriptionTab = ({ userProfile, setSuccess, setError }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const [pricingTab, setPricingTab] = useState(1);
  const [loading, setLoading] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [showCancelOption, setShowCancelOption] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleTabChange = (event, newValue) => {
    if (typeof newValue === 'number') {
      setPricingTab(newValue);
    }
  };

  const handleUpgrade = () => {
    if (!currentUser) {
      setError('Vous devez être connecté pour effectuer un achat');
      return;
    }

    const currentPlan = subscriptionInfo.plan;
    if (currentPlan === 'monthly' && pricingTab === 0) {
      setError('Vous avez déjà un abonnement mensuel actif');
      return;
    }
    if (currentPlan === 'annual' && pricingTab === 1) {
      setError('Vous avez déjà un abonnement annuel actif');
      return;
    }

    const baseUrls = {
      // TODO: Update these URLs with new Stripe products for 29.99€/month and 300€/year
      0: 'https://buy.stripe.com/eVq3cw5CifG34PDePk0oM02', // Monthly 29.99€ - UPDATE NEEDED
      1: 'https://buy.stripe.com/5kQ8wQ7KqalJ81P5eK0oM03'  // Annual 300€ - UPDATE NEEDED
    };
    
    const stripeUrl = `${baseUrls[pricingTab]}?client_reference_id=${currentUser.uid}`;
    window.open(stripeUrl, '_blank');
  };

  const handleCancelSubscription = async (feedbackData) => {
    setLoading(true);
    try {
      const functions = getFunctions();
      const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
      
      // Include feedback data in the cancellation request
      const result = await cancelSubscription({ feedback: feedbackData });
      
      if (result.data.success) {
        const endDate = new Date(result.data.currentPeriodEnd).toLocaleDateString('fr-FR');
        setSuccess(`Votre abonnement sera annulé le ${endDate}. Il restera actif jusqu'à cette date. Merci pour vos retours !`);
      } else {
        setError('Erreur lors de l\'annulation de l\'abonnement');
      }
      
      setCancelModalOpen(false);
      setShowCancelOption(false);
    } catch (error) {
      console.error('Error canceling subscription:', error);
      setError('Erreur lors de l\'annulation de l\'abonnement. Contactez le support si le problème persiste.');
    } finally {
      setLoading(false);
    }
  };

  const premiumFeatures = [
    {
      icon: <ScheduleIcon sx={{ fontSize: 28 }} />,
      title: 'Planning de rendez-vous intelligent',
      description: 'Système de réservation en ligne automatisé avec gestion des créneaux et notifications',
      category: 'Gestion'
    },
    {
      icon: <EmailIcon sx={{ fontSize: 28 }} />,
      title: 'Marketing automation',
      description: 'Emails automatiques de rappel, confirmation et suivi client personnalisés',
      category: 'Marketing'
    },
    {
      icon: <StarIcon sx={{ fontSize: 28 }} />,
      title: 'Boost SEO & Réputation',
      description: 'Invitations automatiques aux avis Google pour améliorer votre visibilité',
      category: 'Visibilité'
    },
    {
      icon: <QrCodeIcon sx={{ fontSize: 28 }} />,
      title: 'QR Code professionnel',
      description: 'Code QR personnalisé pour votre établissement avec analytics intégrés',
      category: 'Digital'
    },
    {
      icon: <LanguageIcon sx={{ fontSize: 28 }} />,
      title: 'Site web professionnel',
      description: 'Fiche publique optimisée SEO avec présentation complète de vos services',
      category: 'Web'
    },
    {
      icon: <SearchIcon sx={{ fontSize: 28 }} />,
      title: 'Référencement premium',
      description: 'Présence prioritaire sur BookingBarber avec badge professionnel vérifié',
      category: 'Visibilité'
    }
  ];

  const getSubscriptionStatus = () => {
    if (!userProfile.subscription) return { status: 'Aucun abonnement', color: 'default', active: false, plan: null };
    
    const { plan, active, endDate } = userProfile.subscription;
    const end = endDate?.toDate ? endDate.toDate() : new Date(endDate);
    const now = new Date();
    
    if (!active || end <= now) {
      return { status: 'Abonnement expiré', color: 'error', active: false, plan: null };
    }
    
    if (plan === 'trial') {
      const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      return {
        status: `Essai gratuit (${daysLeft} jour${daysLeft > 1 ? 's' : ''} restant${daysLeft > 1 ? 's' : ''})`,
        color: daysLeft <= 3 ? 'warning' : 'info',
        active: true,
        daysLeft,
        plan: 'trial'
      };
    }
    
    return { status: `Abonnement ${plan} actif`, color: 'success', active: true, plan };
  };

  const subscriptionInfo = getSubscriptionStatus();
  
  const getSubscriptionOptions = () => {
    const currentPlan = subscriptionInfo.plan;
    
    if (!subscriptionInfo.active || currentPlan === 'trial' || !currentPlan) {
      return {
        showUpgrade: true,
        showCancel: false,
        showMonthly: true,
        showAnnual: true,
        title: 'Choisir votre abonnement'
      };
    } else if (currentPlan === 'monthly') {
      return {
        showUpgrade: true,
        showCancel: true,
        showMonthly: false,
        showAnnual: true,
        title: 'Optimiser votre abonnement'
      };
    } else if (currentPlan === 'annual') {
      return {
        showUpgrade: false,
        showCancel: true,
        showMonthly: false,
        showAnnual: false,
        title: 'Gérer votre abonnement premium'
      };
    }
    
    return {
      showUpgrade: true,
      showCancel: false,
      showMonthly: true,
      showAnnual: true,
      title: 'Upgrader votre abonnement'
    };
  };

  const subscriptionOptions = getSubscriptionOptions();

  useEffect(() => {
    if (subscriptionInfo.plan === 'monthly') {
      setPricingTab(1);
    }
  }, [subscriptionInfo.plan]);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 1, sm: 2 }, px: { xs: 1, sm: 3 } }}>
      {/* Hero Section */}
      <Fade in timeout={800}>
        <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 6 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
              mb: 2
            }}
          >
            Abonnement Professionnel
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: { xs: '100%', sm: 600 },
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              px: { xs: 2, sm: 0 }
            }}
          >
            Développez votre activité avec notre suite d'outils professionnels conçue pour les entrepreneurs du bien-être
          </Typography>
        </Box>
      </Fade>

      {/* Subscription Plans - MOVED TO TOP */}
      <Card sx={{ mb: { xs: 3, sm: 6 }, overflow: 'visible' }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
          color: 'white',
          p: { xs: 2, sm: 4 },
          textAlign: 'center'
        }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }
            }}
          >
            {subscriptionOptions.title}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              fontWeight: 400,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            Transformez votre business dès aujourd'hui
          </Typography>
        </Box>

        <CardContent sx={{ p: { xs: 1, sm: 4 } }}>

          {/* Pricing Tabs */}
          {subscriptionOptions.showUpgrade && (
            <>
              {subscriptionOptions.showMonthly && subscriptionOptions.showAnnual && (
                <Box display="flex" justifyContent="center" mb={{ xs: 2, sm: 4 }} px={{ xs: 1, sm: 0 }}>
                  <Paper
                    elevation={3}
                    sx={{
                      borderRadius: 4,
                      p: 0.5,
                      backgroundColor: '#f8f9fa',
                      width: { xs: '100%', sm: 'auto' },
                      maxWidth: 320
                    }}
                  >
                    <Tabs
                      value={pricingTab}
                      onChange={handleTabChange}
                      variant={isMobile ? "fullWidth" : "standard"}
                      sx={{
                        '& .MuiTab-root': {
                          borderRadius: 3,
                          textTransform: 'none',
                          fontWeight: 600,
                          minWidth: { xs: 0, sm: 140 },
                          py: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.85rem', sm: '1rem' },
                          flex: { xs: 1, sm: 'none' }
                        },
                        '& .Mui-selected': {
                          backgroundColor: 'white',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          color: 'primary.main'
                        },
                        '& .MuiTabs-indicator': {
                          display: 'none'
                        }
                      }}
                    >
                      <Tab label="Mensuel" />
                      <Tab label="Annuel" />
                    </Tabs>
                  </Paper>
                </Box>
              )}

              {/* Pricing Cards */}
              {((subscriptionOptions.showMonthly && pricingTab === 0) || (subscriptionOptions.showAnnual && pricingTab === 1)) && (
                <Box
                  display="flex"
                  justifyContent="center"
                  mb={{ xs: 2, sm: 4 }}
                  px={{ xs: 0, sm: 0 }}
                  position="relative"
                >
                  {pricingTab === 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10,
                        width: 'auto'
                      }}
                    >
                      <Chip
                        icon={<AutoAwesomeIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />}
                        label={isMobile ? "RECOMMANDÉ" : "RECOMMANDÉ - ÉCONOMISEZ 96€"}
                        onClick={() => {}} // Empty function to prevent onClick error
                        sx={{
                          backgroundColor: '#059669',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: { xs: '0.65rem', sm: '0.85rem' },
                          height: { xs: 24, sm: 32 },
                          '& .MuiChip-label': {
                            px: { xs: 1, sm: 1.5 }
                          },
                          '& .MuiChip-icon': {
                            marginLeft: { xs: 0.5, sm: 1 }
                          }
                        }}
                      />
                    </Box>
                  )}
                  <Card
                    elevation={pricingTab === 1 ? 16 : 8}
                    sx={{
                      maxWidth: { xs: '100%', sm: 480 },
                      width: '100%',
                      border: pricingTab === 1 ? '3px solid #059669' : '2px solid #e5e7eb',
                      position: 'relative',
                      background: pricingTab === 1
                        ? 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)'
                        : 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                      transform: { xs: 'scale(1)', sm: pricingTab === 1 ? 'scale(1.02)' : 'scale(1)' },
                      transition: 'all 0.3s ease',
                      mt: pricingTab === 1 ? { xs: 1, sm: 1.5 } : 0
                    }}
                  >
                    
                    <CardContent sx={{ p: { xs: 2, sm: 5 }, textAlign: 'center' }}>
                      <Box display="flex" alignItems="center" justifyContent="center" gap={{ xs: 1, sm: 2 }} mb={{ xs: 2, sm: 3 }}>
                        {pricingTab === 0 ? (
                          <ScheduleIcon sx={{ fontSize: { xs: 24, sm: 36 }, color: 'primary.main' }} />
                        ) : (
                          <TrendingUpIcon sx={{ fontSize: { xs: 24, sm: 36 }, color: '#059669' }} />
                        )}
                        <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2rem' } }}>
                          {pricingTab === 0 ? 'Mensuel' : 'Annuel'}
                        </Typography>
                      </Box>
                      
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                        {pricingTab === 0 ? 'Flexibilité maximale' : 'Meilleure valeur'}
                      </Typography>
                      
                      <Box my={{ xs: 2, sm: 4 }}>
                        <Typography
                          variant="h1"
                          sx={{
                            fontWeight: 800,
                            color: pricingTab === 1 ? '#059669' : '#1976d2',
                            fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                            lineHeight: 1
                          }}
                        >
                          {pricingTab === 0 ? '29.99€' : '25€'}
                        </Typography>
                        <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 500, fontSize: { xs: '1rem', sm: '1.5rem' } }}>
                          par mois
                        </Typography>
                        
                        {pricingTab === 1 && (
                          <Box mt={{ xs: 2, sm: 3 }}>
                            <Typography variant="h6" sx={{ textDecoration: 'line-through', color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                              359.88€ en mensuel
                            </Typography>
                            <Typography variant="h5" sx={{ color: '#059669', fontWeight: 700, fontSize: { xs: '1rem', sm: '1.5rem' } }}>
                              300€ facturés annuellement
                            </Typography>
                            <Chip
                              label="Économie de 59.88€"
                              onClick={() => {}} // Empty function to prevent onClick error
                              sx={{
                                mt: 1,
                                fontWeight: 600,
                                backgroundColor: '#059669',
                                color: 'white',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                              }}
                            />
                          </Box>
                        )}
                      </Box>

                      {pricingTab === 1 && (
                        <Box mb={{ xs: 2, sm: 4 }}>
                          <Stack spacing={{ xs: 1, sm: 2 }}>
                            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                              <CheckCircleIcon sx={{ color: '#059669', fontSize: { xs: 20, sm: 24 } }} />
                              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                2 mois offerts
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                              <SecurityIcon sx={{ color: '#059669', fontSize: { xs: 20, sm: 24 } }} />
                              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                Tranquillité d'esprit pour 12 mois
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      )}

                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={handleUpgrade}
                        sx={{
                          py: { xs: 1.5, sm: 2.5 },
                          backgroundColor: pricingTab === 1 ? '#059669' : '#1976d2',
                          fontSize: { xs: '0.9rem', sm: '1.2rem' },
                          fontWeight: 700,
                          borderRadius: 3,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                          '&:hover': {
                            backgroundColor: pricingTab === 1 ? '#047857' : '#1565c0',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 32px rgba(0,0,0,0.3)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {subscriptionInfo.plan === 'monthly' && pricingTab === 1
                          ? 'Passer à l\'annuel'
                          : 'Commencer maintenant'
                        }
                      </Button>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' }, textAlign: 'center' }}>
                        <SecurityIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1 }} />
                        Paiement sécurisé • Annulation possible à tout moment
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </>
          )}

          {/* Success Message for Annual Users */}
          {subscriptionInfo.plan === 'annual' && !subscriptionOptions.showUpgrade && (
            <Card sx={{ 
              background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
              border: '3px solid #059669',
              textAlign: 'center'
            }}>
              <CardContent sx={{ py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 64, color: '#059669', mb: 2 }} />
                <Typography variant="h4" gutterBottom sx={{ color: '#047857', fontWeight: 700 }}>
                  Excellent choix !
                </Typography>
                <Typography variant="h6" sx={{ color: '#047857', mb: 2 }}>
                  Vous bénéficiez du meilleur plan avec 96€ d'économies annuelles
                </Typography>
                <Typography variant="body1" sx={{ color: '#047857' }}>
                  Profitez de toutes les fonctionnalités premium sans interruption
                </Typography>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      {/* Current Status Card - MOVED AFTER SUBSCRIPTION PLANS */}
      <Slide direction="up" in timeout={600}>
        <Card sx={{ 
          mb: 6,
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 200,
            height: 200,
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            transform: 'translate(50%, -50%)'
          }} />
          <CardContent sx={{ p: { xs: 2, sm: 4 }, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <InfoIcon sx={{ fontSize: 32 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                Statut de votre abonnement
              </Typography>
            </Box>
            <Box sx={{ 
              backgroundColor: 'rgba(255,255,255,0.15)', 
              borderRadius: 2, 
              p: { xs: 2, sm: 3 },
              backdropFilter: 'blur(10px)'
            }}>
              <SubscriptionStatus user={userProfile} />
            </Box>
          </CardContent>
        </Card>
      </Slide>

      {/* Features Section - MOVED TO BOTTOM */}
      <Card sx={{ mb: { xs: 3, sm: 6 } }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
          color: 'white',
          p: { xs: 2, sm: 4 },
          textAlign: 'center'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Fonctionnalités Premium Incluses
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Tous les outils dont vous avez besoin pour développer votre activité
          </Typography>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Grid container spacing={{ xs: 2, sm: 4 }}>
            {premiumFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      elevation: 8,
                      transform: { xs: 'none', sm: 'translateY(-4px)' }
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
                    <Box display="flex" alignItems="flex-start" gap={{ xs: 2, sm: 3 }}>
                      <Box
                        sx={{
                          background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                          color: 'white',
                          borderRadius: 3,
                          p: { xs: 1.5, sm: 2 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: { xs: 48, sm: 64 },
                          minHeight: { xs: 48, sm: 64 }
                        }}
                      >
                        {React.cloneElement(feature.icon, {
                          sx: { fontSize: { xs: 20, sm: 28 } }
                        })}
                      </Box>
                      <Box flex={1}>
                        <Chip
                          label={feature.category}
                          size="small"
                          sx={{
                            mb: 1,
                            fontWeight: 600,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            cursor: 'default'
                          }}
                          color="primary"
                          variant="outlined"
                          onClick={() => {}} // Fonction vide pour éviter l'erreur
                        />
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {feature.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Support Section */}
      <Card sx={{
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: 'white'
      }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
          <SupportIcon sx={{ fontSize: { xs: 40, sm: 56 }, mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Support Premium Inclus
          </Typography>
          <Typography variant="body1" sx={{
            mb: 3,
            opacity: 0.9,
            maxWidth: 600,
            mx: 'auto',
            fontSize: { xs: '0.9rem', sm: '1rem' },
            px: { xs: 1, sm: 0 }
          }}>
            Notre équipe d'experts est disponible pour vous accompagner dans la configuration
            et l'optimisation de votre abonnement professionnel
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<SupportIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />}
            onClick={() => window.location.href = 'mailto:support@omnivers.fr?subject=Paiement- BookingBarber'}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.3)',
              color: 'white',
              fontWeight: 600,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              py: { xs: 1, sm: 1.5 },
              px: { xs: 2, sm: 3 },
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)',
                transform: { xs: 'none', sm: 'translateY(-2px)' }
              }
            }}
          >
            Contacter le Support
          </Button>
        </CardContent>
      </Card>

      {/* Hidden Cancel Option - Very discrete at bottom */}
      {subscriptionOptions.showCancel && (
        <Box sx={{
          mt: 6,
          pt: 3,
          borderTop: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          {!showCancelOption ? (
            <Button
              variant="text"
              size="small"
              onClick={() => setShowCancelOption(true)}
              sx={{
                color: '#9ca3af',
                fontSize: '0.75rem',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#6b7280'
                }
              }}
            >
              {t('cancelModal.manageCancellation')}
            </Button>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                {t('cancelModal.wantToCancel')}
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setCancelModalOpen(true)}
                sx={{
                  fontSize: '0.8rem',
                  py: 0.5,
                  px: 2
                }}
              >
                {t('cancelModal.cancelSubscriptionButton')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Cancel Modal */}
      <CancelSubscriptionModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={handleCancelSubscription}
        loading={loading}
      />
    </Container>
  );
};

export default SubscriptionTab;
